// AIDEV-NOTE: product-copy; the customer-facing nouns for the two products, in one place.
// Parking shipped with the noun inlined at each site (`isParking ? 'parking space' : 'storage
// unit'`), which is how the waitlist dialog kept announcing "storage unit" over a parking space
// long after the card stopped: a new surface has no way of knowing the question exists. Reach for
// these helpers instead of writing the ternary again, and a missed surface becomes a grep away.
//
// Deliberately not a lookup on the unit: callers hold a `UnitType`, a `ProductType`, or nothing
// at all (a dialog whose unit is still loading), so the parameter is an optional ProductType and
// the storage wording is the default. That default matches every pre-parking caller.

import type { ProductType } from '@/shared/model'

/**
 * What to call the thing being rented, in full — "storage unit" or "parking space".
 *
 * Use in a sentence the customer reads: "Join the waitlist for this parking space".
 */
export function productNoun(productType?: ProductType): string {
  return productType === 'PARKING' ? 'parking space' : 'storage unit'
}

/**
 * The bare noun — "unit" or "space" — for a sentence that has already established the product.
 *
 * "We'll notify you when this space becomes available" reads better than repeating "parking
 * space" three times in one card.
 */
export function productShortNoun(productType?: ProductType): string {
  return productType === 'PARKING' ? 'space' : 'unit'
}

/**
 * The bare noun capitalised, for a label that leads with it — "Unit A101", "Space RV-101".
 */
export function productShortNounCapitalized(productType?: ProductType): string {
  return productType === 'PARKING' ? 'Space' : 'Unit'
}

/**
 * Whether square footage belongs on screen for this product.
 *
 * AIDEV-NOTE: no-sq-ft-on-parking; an open lot is measured by the length that fits the vehicle,
 * not by floor area, and "300 sq ft" invites a comparison to enclosed storage that does not
 * hold (spec §9 — the same reasoning ParkingUnitCard states for dropping it from the card).
 */
export function showsSquareFootage(productType?: ProductType): boolean {
  return productType !== 'PARKING'
}
